import React, { useRef, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { A11y, Keyboard, Navigation, Pagination } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper';
import clsx from 'clsx';

import { MContentHeader } from '../../molecules/MContentHeader';
import { MCarouselControls } from '../../molecules/MCarouselControls';
import { MCarouselControlsType } from '../../molecules/MCarouselControls/MCarouselControls.types';
import { AImage } from '../../atoms/AImage';
import { AspectRatio, MediaQuery, ResponsiveImageSpec, generateImageRenditions } from '../../utils/ImageUtils';

import {
  Container,
  TriptychWrapper,
  TriptychSlide,
  HeaderWrapper,
  ControlsContainer,
} from './OCarouselTriptych.styles';
import type { MCarouselControlsConfig, OCarouselTriptychProps } from './OCarouselTriptych.types';
import { OCarouselTriptychControlType, OCarouselTriptychImageOrientation } from './OCarouselTriptych.types';

import 'swiper/css';

const COMPONENT_NAME = 'o-web-ui-kit-OCarouselTriptych';
const TEST_ID = 'web-ui-kit-OCarouselTriptych';

export const MIN_LOOP_SLIDES = 6;

const HORIZONTAL_IMAGE_SPECS: ResponsiveImageSpec[] = [
  { media: MediaQuery.MAX_WIDTH_SM, aspectRatio: AspectRatio.CLASSIC_HOR, imgWidthPx: 283 },
  { media: MediaQuery.MIN_WIDTH_MD, aspectRatio: AspectRatio.CLASSIC_HOR, imgWidthPx: 473 },
  { media: MediaQuery.MIN_WIDTH_XL, aspectRatio: AspectRatio.CLASSIC_HOR, imgWidthPx: 754 },
];

const VERTICAL_IMAGE_SPECS: ResponsiveImageSpec[] = [
  { media: MediaQuery.MAX_WIDTH_SM, aspectRatio: AspectRatio.CLASSIC_VER, imgWidthPx: 126 },
  { media: MediaQuery.MIN_WIDTH_MD, aspectRatio: AspectRatio.CLASSIC_VER, imgWidthPx: 210 },
  { media: MediaQuery.MIN_WIDTH_XL, aspectRatio: AspectRatio.CLASSIC_VER, imgWidthPx: 335 },
];

interface ResolvedControls {
  type: MCarouselControlsType;
  imageOverlay: boolean;
}

const isTruthy = (value: boolean | string | undefined): boolean => value === true || value === 'true';

export function resolveControls(config: MCarouselControlsConfig | undefined): ResolvedControls {
  if (!config) {
    return { type: MCarouselControlsType.Dots, imageOverlay: false };
  }

  const imageOverlay = isTruthy(config.imageOverlay);
  // AEM may send the empty string when the dropdown is unset; treat it the
  // same as `undefined` so we silently fall back to Dots without warning.
  const requestedType = config.type === ('' as unknown as typeof config.type) ? undefined : config.type;

  let type: MCarouselControlsType;
  switch (requestedType) {
    case OCarouselTriptychControlType.Dots:
    case undefined:
      type = MCarouselControlsType.Dots;
      break;
    case OCarouselTriptychControlType.Numbers:
    case OCarouselTriptychControlType.Digits:
      type = MCarouselControlsType.Numbers;
      break;
    default:
      if (process.env['NODE_ENV'] !== 'production') {
        console.warn(
          `[OCarouselTriptych] mCarouselControls.type "${requestedType}" is not supported. ` +
            `Allowed: "Dots", "Numbers" (or legacy "Digits"). Falling back to "Dots".`
        );
      }
      type = MCarouselControlsType.Dots;
  }

  return { type, imageOverlay };
}

export const OCarouselTriptych: React.FC<OCarouselTriptychProps> = ({
  images,
  onSlideChange,
  onSlideClick,
  spaceBetween = 16,
  imageOrientation = OCarouselTriptychImageOrientation.Horizontal,
  mContentHeader,
  mCarouselControls,
  swiperOptions = {},
  className,
}) => {
  const isVertical = imageOrientation === OCarouselTriptychImageOrientation.Vertical;
  const responsiveImageSpecs = isVertical ? VERTICAL_IMAGE_SPECS : HORIZONTAL_IMAGE_SPECS;
  const resolvedControls = resolveControls(mCarouselControls);

  const swiperRef = useRef<SwiperType | null>(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [totalSlides, setTotalSlides] = useState(images.length);

  const handleSwiperInit = (swiper: SwiperType) => {
    swiperRef.current = swiper;
    // Pagination controls always reflect the original authored image count,
    // not the internally duplicated slide list.
    setTotalSlides(images.length);
  };

  const handleSlideChange = (swiper: SwiperType) => {
    // Swiper's `realIndex` (in loop mode) and `activeIndex` both point into the
    // rendered slide list. When we duplicate the images array below to fill the
    // triptych peek, we map the rendered index back to the original image index
    // with a modulo so callers always receive the authored index. When no
    // duplication happened, the raw index is already correct — pass it through
    // unchanged so consumer semantics stay identical to prior behavior.
    const renderedIndex = swiper.params?.loop ? swiper.realIndex : swiper.activeIndex;
    const originalIndex =
      renderedImages.length > images.length && images.length > 0 ? renderedIndex % images.length : renderedIndex;
    setCurrentSlide(originalIndex);
    onSlideChange?.(originalIndex);
  };

  const handlePrev = () => swiperRef.current?.slidePrev();
  const handleNext = () => swiperRef.current?.slideNext();

  const carouselAriaLabel = mContentHeader?.mTextBlockSimple?.aHeader?.text || 'Image carousel';

  const enableLoop = images.length > 1;
  // Repeat the authored images internally when there are too few for the
  // triptych peek layout. See `MIN_LOOP_SLIDES` above for the full rationale.
  const renderedImages = React.useMemo(() => {
    if (!enableLoop || images.length === 0 || images.length >= MIN_LOOP_SLIDES) {
      return images;
    }
    const repeats = Math.ceil(MIN_LOOP_SLIDES / images.length);
    return Array.from({ length: repeats }, () => images).flat();
  }, [images, enableLoop]);

  const defaultSwiperOptions = {
    spaceBetween,
    centeredSlides: true,
    slidesPerView: 'auto' as const,
    loop: enableLoop,
    keyboard: { enabled: true },
  };

  const finalSwiperOptions = { ...defaultSwiperOptions, ...swiperOptions };

  return (
    <Container
      data-component-name={COMPONENT_NAME}
      data-testid={TEST_ID}
      className={clsx('m-container-fullbleed', className)}
      aria-label={carouselAriaLabel}
    >
      <HeaderWrapper>
        <MContentHeader {...mContentHeader} />
      </HeaderWrapper>
      <TriptychWrapper $orientation={imageOrientation}>
        <Swiper
          modules={[A11y, Keyboard, Navigation, Pagination]}
          onSwiper={handleSwiperInit}
          onSlideChange={handleSlideChange}
          {...finalSwiperOptions}
        >
          {renderedImages.map((imageProps, index) => {
            const originalIndex =
              renderedImages.length > images.length && images.length > 0 ? index % images.length : index;
            return (
              <SwiperSlide key={[imageProps.image?.assetPath || imageProps.src, index].join('-')}>
                <TriptychSlide onClick={() => onSlideClick?.(originalIndex)}>
                  <AImage
                    {...imageProps}
                    renditions={generateImageRenditions(responsiveImageSpecs, imageProps.image?.assetPath)}
                  />
                </TriptychSlide>
              </SwiperSlide>
            );
          })}
        </Swiper>
        {images.length > 1 && (
          <ControlsContainer $orientation={imageOrientation}>
            <MCarouselControls
              type={resolvedControls.type}
              imageOverlay={resolvedControls.imageOverlay}
              totalSlides={totalSlides}
              currentSlide={currentSlide}
              onPrev={handlePrev}
              onNext={handleNext}
              loop={enableLoop}
            />
          </ControlsContainer>
        )}
      </TriptychWrapper>
    </Container>
  );
};

export * from './OCarouselTriptych.types';
